import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { findOcrMatches, ocrFilename, searchTokens } from "../utils/ocrSearch";

const cache = new Map();
export const clearOcrSearchCache = () => cache.clear();

export default function useOcrSearch(url, query, mode, enabled) {
  const filename = ocrFilename(url);
  const active = enabled && !!filename && searchTokens(query || "").length > 0;
  const [result, setResult] = useState({ filename: "", status: "idle", data: null });
  useEffect(() => {
    if (!active) return;
    if (cache.has(filename)) {
      setResult({ filename, status: "ready", data: cache.get(filename) });
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    setResult({ filename, status: "loading", data: null });
    axios.get(`${process.env.REACT_APP_BASE_URL}/ocr-search/${encodeURIComponent(filename)}`, {
      signal: controller.signal, timeout: 20000,
    }).then(({ data }) => {
      if (cancelled) return;
      if (data.version !== 1 || data.coordinate_system !== "normalized_top_left" || !Array.isArray(data.pages)) {
        throw new Error("Unsupported OCR response");
      }
      cache.set(filename, data);
      if (cache.size > 20) cache.delete(cache.keys().next().value);
      setResult({ filename, status: "ready", data });
    }).catch(() => {
      if (!cancelled) setResult({ filename, status: "unavailable", data: null });
    });
    return () => { cancelled = true; controller.abort(); };
  }, [filename, active]);
  // Never paint a previous document's coordinates while a new request loads.
  const data = active && result.filename === filename ? result.data : null;
  const matches = useMemo(() => findOcrMatches(data, query || "", mode), [data, query, mode]);
  return { data, matches, status: !active ? "idle" : result.filename !== filename ? "loading" : result.status };
}
